import type { Question } from "../../data/assessmentData";

export const assessmentQuestionsFr: Question[] = [
  // Stratégie & Pilotage (5 questions)
  {
    id: 1,
    pillar: "Stratégie & Pilotage",
    question: "Votre positionnement est-il clair ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Si vous posez la question à trois personnes en interne, vous aurez quatre réponses différentes.",
      },
      {
        id: "b",
        level: 2,
        text: "Il y a une **idée générale**, mais chacun la reformule à sa sauce. Le site dit une chose, les commerciaux une autre.",
      },
      {
        id: "c",
        level: 3,
        text: "Le positionnement est **défini et documenté**. L'équipe le connaît, mais il n'est pas toujours décliné dans chaque support.",
      },
      {
        id: "d",
        level: 4,
        text: "Positionnement **clair, partagé** en interne et reflété partout. Les clients le confirment spontanément.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 2,
    pillar: "Stratégie & Pilotage",
    question: "Votre stratégie d'acquisition est-elle structurée ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre stratégie d'acquisition, c'est le bouche-à-oreille et l'espoir que le téléphone sonne.",
      },
      {
        id: "b",
        level: 2,
        text: "On fait des **actions ponctuelles** (salon, campagne LinkedIn) mais sans plan d'ensemble ni objectifs clairs.",
      },
      {
        id: "c",
        level: 3,
        text: "Un plan existe avec des **canaux identifiés**, un budget et des campagnes récurrentes. Les résultats sont suivis, mais **pas reliés au pipeline**.",
      },
      {
        id: "d",
        level: 4,
        text: "**Stratégie documentée** par canal, avec objectifs chiffrés, budget, campagnes actives et suivi mensuel **relié au pipeline**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 3,
    pillar: "Stratégie & Pilotage",
    question:
      "Comment se passe la collaboration entre le marketing et les ventes ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Les ventes disent que les leads sont mauvais. Le marketing dit que les vendeurs ne rappellent pas. C'est compliqué.",
      },
      {
        id: "b",
        level: 2,
        text: "On s'échange des infos **de temps en temps**, mais le marketing ne sait pas toujours ce que les commerciaux font des leads, et vice versa.",
      },
      {
        id: "c",
        level: 3,
        text: "**Réunions régulières** et définition commune d'un bon lead. Mais les outils et les données ne sont pas encore partagés.",
      },
      {
        id: "d",
        level: 4,
        text: "CRM commun, réunions régulières, reporting partagé. Les deux équipes **avancent avec les mêmes KPI**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 4,
    pillar: "Stratégie & Pilotage",
    question:
      "Comment décidez-vous de ce qui est prioritaire en marketing quand tout semble urgent ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "La priorité, c'est ce que le CEO a vu chez un concurrent ce matin, ou ce que le commercial le plus vocal réclame.",
      },
      {
        id: "b",
        level: 2,
        text: "On a une idée des priorités, mais elles **changent souvent** au gré des urgences et des demandes internes.",
      },
      {
        id: "c",
        level: 3,
        text: "Priorités **définies en début de trimestre**. Des ajustements sont faits, mais le cap reste clair.",
      },
      {
        id: "d",
        level: 4,
        text: "Priorisation sur critères objectifs (impact, effort, alignement stratégique). **Plan trimestriel revu** avec la direction.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 5,
    pillar: "Stratégie & Pilotage",
    question:
      "Avez-vous des tableaux de bord qui montrent la performance de vos actions marketing en un coup d'œil ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre tableau de bord, c'est un feeling général et un coup d'œil à Google Analytics une fois par trimestre.",
      },
      {
        id: "b",
        level: 2,
        text: "On regarde les stats de chaque outil séparément (Analytics, LinkedIn, emailing) mais **sans vue consolidée**.",
      },
      {
        id: "c",
        level: 3,
        text: "Un tableau de bord regroupe les **KPIs principaux**. Il est consulté régulièrement mais mis à jour manuellement.",
      },
      {
        id: "d",
        level: 4,
        text: "**Dashboard automatisé** regroupant toutes les sources, consulté chaque semaine et utilisé pour piloter les décisions.",
      },
    ],
    correctAnswer: "d",
  },

  // Marque & Expérience (6 questions)
  {
    id: 6,
    pillar: "Marque & Expérience",
    question:
      "Votre image est-elle cohérente et reconnaissable sur votre site, vos imprimés et vos réseaux sociaux ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "On a un logo. Enfin, trois. Plus celui que quelqu'un a refait dans PowerPoint pour un salon.",
      },
      {
        id: "b",
        level: 2,
        text: "On a un logo et des couleurs définies, mais **chacun bricole** ses présentations et ses visuels à sa manière.",
      },
      {
        id: "c",
        level: 3,
        text: "Une **charte graphique** existe et la plupart des supports la respectent. Il reste quelques écarts (anciens documents, templates non mis à jour).",
      },
      {
        id: "d",
        level: 4,
        text: "Charte complète et **appliquée partout** : site, réseaux, présentations, signatures, imprimés. Chaque support renforce la marque.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 7,
    pillar: "Marque & Expérience",
    question:
      "Vos supports (brochures, présentations, fiches produit) aident-ils vos commerciaux à vendre ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Nos commerciaux refont leur propre PowerPoint à chaque rendez-vous. Personne n'utilise les mêmes documents.",
      },
      {
        id: "b",
        level: 2,
        text: "Il y a des supports, mais ils **datent** ou ne correspondent plus à ce qu'on vend vraiment.",
      },
      {
        id: "c",
        level: 3,
        text: "Les supports sont **à jour et utilisés**. Ils pourraient être plus percutants ou mieux adaptés à certaines cibles.",
      },
      {
        id: "d",
        level: 4,
        text: "Les supports sont déclinés par cible et par étape du cycle de vente et ils **convertissent**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 8,
    pillar: "Marque & Expérience",
    question:
      "Un visiteur sur votre site comprend-il en quelques secondes ce que vous faites ?",
    options: [
      {
        id: "a",
        level: 1,
        text: 'Notre page d\'accueil dit "Solutions innovantes pour un monde en mouvement". Personne ne sait ce que ça veut dire.',
      },
      {
        id: "b",
        level: 2,
        text: "Le site explique nos services, mais **il faut fouiller** pour comprendre ce qu'on fait concrètement.",
      },
      {
        id: "c",
        level: 3,
        text: "Le **message est clair**, mais le parcours pourrait mieux guider les visiteurs vers une action concrète.",
      },
      {
        id: "d",
        level: 4,
        text: "Message clair dès la page d'accueil, **parcours pensé** par cible, appels à l'action qui convertissent.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 9,
    pillar: "Marque & Expérience",
    question: "Utilisez-vous un CRM pour suivre vos prospects et clients ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre CRM, c'est un fichier Excel que personne ne met à jour et dont il existe 4 versions sur le serveur.",
      },
      {
        id: "b",
        level: 2,
        text: "On a un **CRM**, mais seule une partie de l'équipe l'utilise. Les données ne sont **pas à jour**.",
      },
      {
        id: "c",
        level: 3,
        text: "Le CRM est **utilisé par une partie de l'équipe**. Le marketing et les ventes n'y collaborent pas vraiment.",
      },
      {
        id: "d",
        level: 4,
        text: "CRM **centralisé, utilisé** par les ventes ET le marketing. Contacts segmentés, historique complet, données fiables.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 10,
    pillar: "Marque & Expérience",
    question:
      "Vos outils marketing (site, emailing, CRM, analytics) se parlent-ils entre eux ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Pour savoir ce qui se passe, il faut six mots de passe, quatre onglets et un talent pour le copier-coller.",
      },
      {
        id: "b",
        level: 2,
        text: "Quelques connexions existent (formulaire du site → CRM) mais **beaucoup se fait manuellement**.",
      },
      {
        id: "c",
        level: 3,
        text: "Les **outils principaux** sont **connectés** mais certaines données doivent être exportées/importées à la main.",
      },
      {
        id: "d",
        level: 4,
        text: "Écosystème intégré : site, CRM, emailing et analytics communiquent **automatiquement**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 11,
    pillar: "Marque & Expérience",
    question: "Votre site est-il performant techniquement ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre site met tellement de temps à charger qu'on a le temps de se faire un café en attendant. Sur mobile, c'est pire.",
      },
      {
        id: "b",
        level: 2,
        text: "Le site fonctionne globalement, mais **on ne mesure pas** sa vitesse de chargement ou ses scores SEO.",
      },
      {
        id: "c",
        level: 3,
        text: "Le site est **correct techniquement** et adapté au mobile. La vitesse et le SEO technique pourraient être améliorés.",
      },
      {
        id: "d",
        level: 4,
        text: "Site rapide, mobile-first, SEO technique optimisé, avec **suivi régulier des performances**.",
      },
    ],
    correctAnswer: "d",
  },

  // Contenu & Visibilité (6 questions)
  {
    id: 12,
    pillar: "Contenu & Visibilité",
    question:
      "Produisez-vous régulièrement du contenu (articles, posts LinkedIn, vidéos, études de cas, ...) pour attirer vos prospects ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre dernier article de blog date de 2019. Il parle du Covid. Depuis, c'est le silence radio.",
      },
      {
        id: "b",
        level: 2,
        text: "On publie **de temps en temps**, quand quelqu'un a le temps ou l'inspiration. Pas de rythme ni de plan éditorial.",
      },
      {
        id: "c",
        level: 3,
        text: "Un calendrier éditorial existe et **on publie régulièrement**. Le contenu pourrait être plus ciblé.",
      },
      {
        id: "d",
        level: 4,
        text: "**Production industrialisée** : calendrier éditorial, formats variés, publication régulière.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 13,
    pillar: "Contenu & Visibilité",
    question:
      "Vos contenus s'adressent-ils à une cible précise ou parlez-vous à tout le monde ?",
    options: [
      { id: "a", level: 1, text: "Nous on s'adresse à tout le monde." },
      {
        id: "b",
        level: 2,
        text: "Les contenus ne sont **pas adaptés à des profils** spécifiques.",
      },
      {
        id: "c",
        level: 3,
        text: "La plupart des contenus sont **pensés pour nos cibles** principales.",
      },
      {
        id: "d",
        level: 4,
        text: "Contenus adaptés à **chaque profil** et à **chaque étape** du parcours d'achat.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 14,
    pillar: "Contenu & Visibilité",
    question: "Votre contenu se distingue-t-il de celui de vos concurrents ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Si on met le logo d'un concurrent sur nos contenus, honnêtement, personne ne voit la différence.",
      },
      {
        id: "b",
        level: 2,
        text: "On a notre propre contenu, mais le ton et les sujets **ressemblent à ce que font les autres** dans le secteur.",
      },
      {
        id: "c",
        level: 3,
        text: "Notre contenu a une **identité définie** (ton, sujets, angle). Notre défi est de maintenir une cohérence sur tous les canaux.",
      },
      {
        id: "d",
        level: 4,
        text: "Voix distinctive, formats variés, ligne éditoriale identifiable **sur tous les supports de communication**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 15,
    pillar: "Contenu & Visibilité",
    question: "Comment distribuez-vous votre contenu ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "On publie un article sur le site et on espère que quelqu'un le trouve.",
      },
      {
        id: "b",
        level: 2,
        text: "On relaie sur **quelques canaux** (LinkedIn, newsletter) mais sans plan structuré ni recyclage.",
      },
      {
        id: "c",
        level: 3,
        text: "Un **plan de diffusion** existe : chaque contenu est relayé sur plusieurs canaux avec un calendrier. On commence à recycler.",
      },
      {
        id: "d",
        level: 4,
        text: "**Distribution orchestrée** : chaque canal a un rôle défini (notoriété, acquisition, nurturing).",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 16,
    pillar: "Contenu & Visibilité",
    question:
      "Êtes-vous visible et actif sur les canaux où se trouvent vos clients ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "On a créé une page LinkedIn en 2020. Elle a notre logo et une description. Depuis, rien.",
      },
      {
        id: "b",
        level: 2,
        text: "On est trouvable **sur quelques canaux** mais sans que cela résulte d'un effort structuré.",
      },
      {
        id: "c",
        level: 3,
        text: "Présence **visible sur nos canaux principaux** grâce à du contenu régulier.",
      },
      {
        id: "d",
        level: 4,
        text: "**Stratégie multicanale** coordonnée, qui attire des leads qualifiés.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 17,
    pillar: "Contenu & Visibilité",
    question:
      "Vos clients vous trouvent-ils quand ils cherchent vos services ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Si on tape le nom de notre service principal sur Google, on tombe sur nos concurrents. Pour nous trouver, il faut taper notre nom exact.",
      },
      {
        id: "b",
        level: 2,
        text: "On a fait quelques **optimisations de base** pour Google (titres, mots-clés) mais sans stratégie SEO structurée.",
      },
      {
        id: "c",
        level: 3,
        text: "Le **SEO est pris en compte** : mots-clés identifiés, contenus optimisés, positionnement suivi. Il manque une approche systématique.",
      },
      {
        id: "d",
        level: 4,
        text: "**Stratégie SEO complète** pour nous trouver sur Google, mais aussi dans les outils IA comme ChatGPT.",
      },
    ],
    correctAnswer: "d",
  },

  // IA & Outils (6 questions)
  {
    id: 18,
    pillar: "IA & Outils",
    question: "Comment dont gérez-vous vos projets marketing ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Notre outil de gestion de projet, c'est une boîte email pleine et des post-it sur l'écran. Parfois, un fichier Excel partagé.",
      },
      {
        id: "b",
        level: 2,
        text: "On a un outil (Trello, Excel…) mais il n'est **pas utilisé de façon systématique** par toute l'équipe.",
      },
      {
        id: "c",
        level: 3,
        text: "Un **outil** de gestion de projet est en place et **utilisé**. Tâches suivies, deadlines **respectées dans l'ensemble**.",
      },
      {
        id: "d",
        level: 4,
        text: "**Gestion structurée** (Kanban ou sprints), rituels d'équipe, priorités claires, **suivi hebdomadaire**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 19,
    pillar: "IA & Outils",
    question: "Mesurez vous le ROI de votre marketing ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Aucune idée. Le marketing, c'est un acte de foi. On investit et on espère que ça marche.",
      },
      {
        id: "b",
        level: 2,
        text: "On a une **idée générale des coûts**, mais relier un investissement à un résultat business concret reste difficile.",
      },
      {
        id: "c",
        level: 3,
        text: "**Le ROI est mesuré sur les actions principales** (campagnes, salons). Il manque une vue consolidée reliant dépenses et pipeline.",
      },
      {
        id: "d",
        level: 4,
        text: "**ROI** mesuré par canal, relié aux résultats business (leads, rdv, contrats) et **consolidé dans un dashboard**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 20,
    pillar: "IA & Outils",
    question:
      "Utilisez-vous l'IA dans vos activités marketing : rédaction, analyse, visuels, automation ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "L'IA ? On a demandé à ChatGPT de rédiger un email une fois. Ça compte ?",
      },
      {
        id: "b",
        level: 2,
        text: "Quelques personnes utilisent des outils IA **ponctuellement** (ChatGPT, Copilot), mais sans cadre ni partage.",
      },
      {
        id: "c",
        level: 3,
        text: "Des outils IA sont **intégrés dans certains process marketing** (rédaction, analyse, visuels). L'usage est encadré mais pas systématique.",
      },
      {
        id: "d",
        level: 4,
        text: "**IA déployée stratégiquement** : outils intégrés, prompts documentés, gains mesurés.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 21,
    pillar: "IA & Outils",
    question:
      "Votre équipe est-elle formée à l'IA, ou chacun se débrouille dans son coin ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Former l'équipe ? On attend que le stagiaire nous montre comment ça marche.",
      },
      {
        id: "b",
        level: 2,
        text: "**Quelques personnes expérimentent**, mais pas de partage structuré des apprentissages ni de bonnes pratiques.",
      },
      {
        id: "c",
        level: 3,
        text: "Des formations ou ateliers ont eu lieu. **L'équipe connaît les bases**, mais l'adoption reste inégale.",
      },
      {
        id: "d",
        level: 4,
        text: "**Formation continue**, bonnes pratiques **partagées**, veille active. L'IA fait partie de la culture d'équipe.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 22,
    pillar: "IA & Outils",
    question:
      "L'IA vous fait-elle réellement gagner du temps ou de l'argent, de façon mesurable ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "On ne sait pas si l'IA nous fait gagner du temps. On passe tellement de temps à corriger ses outputs que la question se pose.",
      },
      {
        id: "b",
        level: 2,
        text: "**Des gains ponctuels** (rédaction plus rapide, idées de contenu), mais rien de mesuré formellement.",
      },
      {
        id: "c",
        level: 3,
        text: "Des gains sont identifiés et reconnus (productivité, qualité). **On commence à les quantifier.**",
      },
      {
        id: "d",
        level: 4,
        text: "Impact = temps gagné par processus, coût par livrable réduit, qualité améliorée. **Les chiffres justifient l'investissement.**",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 23,
    pillar: "IA & Outils",
    question: "Avez-vous des règles claires sur l'usage de l'IA ?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Des règles ? On n'a même pas de règles claires pour l'utilisation de la machine à café.",
      },
      {
        id: "b",
        level: 2,
        text: "**C'est implicite** : on sait qu'il ne faut pas partager de données confidentielles, mais rien n'est écrit.",
      },
      {
        id: "c",
        level: 3,
        text: "**Des guidelines existent** (données autorisées, validation humaine requise). Communiquées mais pas toujours appliquées.",
      },
      {
        id: "d",
        level: 4,
        text: "**Politique IA documentée et appliquée** : données autorisées, processus de validation, conformité RGPD, revue régulière.",
      },
    ],
    correctAnswer: "d",
  },
];
